import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { statusColor, statusLabel } from '@/lib/biomarkers';
import { BiomarkerRangeChart } from './BiomarkerRangeChart';
import { cn } from '@/lib/utils';

export interface BiomarkerMarker {
  key: string;
  name: string;
  value: number;
  unit: string;
  optimalLow: number;
  optimalHigh: number;
  refLow?: number;
  refHigh?: number;
  status: string;
  researchContext?: string;
  pmids?: string[];
}

interface BiomarkerCardProps {
  marker: BiomarkerMarker;
}

export function BiomarkerCard({ marker }: BiomarkerCardProps) {
  const [expanded, setExpanded] = useState(false);
  const color = statusColor(marker.status);
  const label = statusLabel(marker.status);
  const isFlagged = marker.status !== 'optimal';

  // Mini range indicator: calculate position
  const padding = (marker.optimalHigh - marker.optimalLow) * 0.5;
  const rangeMin = marker.refLow ?? Math.max(0, marker.optimalLow - padding);
  const rangeMax = marker.refHigh ?? marker.optimalHigh + padding;
  const fullRange = rangeMax - rangeMin;
  const clampedValue = Math.min(Math.max(marker.value, rangeMin), rangeMax);
  const markerPct = ((clampedValue - rangeMin) / fullRange) * 100;
  const optLeftPct = ((marker.optimalLow - rangeMin) / fullRange) * 100;
  const optWidthPct =
    ((marker.optimalHigh - marker.optimalLow) / fullRange) * 100;

  return (
    <div
      className={cn(
        'cursor-pointer rounded-xl border bg-white transition-all hover:shadow-sm',
        isFlagged ? 'border-amber-200' : 'border-gray-100'
      )}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Collapsed row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Status dot */}
        <span
          className="h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />

        {/* Name */}
        <span className="flex-1 text-sm font-medium text-navy">
          {marker.name}
        </span>

        {/* Value + unit */}
        <span className="text-sm font-semibold text-navy">
          {marker.value}{' '}
          <span className="text-xs font-normal text-gray-400">
            {marker.unit}
          </span>
        </span>

        {/* Status label */}
        <span
          className="w-24 text-right text-xs font-medium"
          style={{ color }}
        >
          {label}
        </span>

        {/* Mini range bar */}
        <div className="relative hidden h-2 w-20 shrink-0 overflow-hidden rounded-full bg-gray-200 sm:block">
          <div
            className="absolute top-0 h-full rounded-full bg-emerald-200"
            style={{ left: `${optLeftPct}%`, width: `${optWidthPct}%` }}
          />
          <div
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white"
            style={{ left: `${markerPct}%`, backgroundColor: color }}
          />
        </div>

        {/* Chevron */}
        <motion.div
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-gray-400"
        >
          <ChevronDown className="h-4 w-4" />
        </motion.div>
      </div>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-100 px-4 py-4">
              {/* Full range chart */}
              <div className="mb-4">
                <BiomarkerRangeChart
                  markers={[
                    {
                      key: marker.key,
                      name: marker.name,
                      value: marker.value,
                      unit: marker.unit,
                      optimalLow: marker.optimalLow,
                      optimalHigh: marker.optimalHigh,
                      refLow: marker.refLow,
                      refHigh: marker.refHigh,
                      status: marker.status,
                    },
                  ]}
                />
              </div>

              {/* Optimal range text */}
              <div className="mb-3 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                Optimal range: {marker.optimalLow} - {marker.optimalHigh}{' '}
                {marker.unit}
              </div>

              {/* Research context */}
              {marker.researchContext && (
                <div className="mb-3">
                  <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    What this means
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-600">
                    {marker.researchContext}
                  </p>
                </div>
              )}

              {/* Citations */}
              {marker.pmids && marker.pmids.length > 0 && (
                <div>
                  <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Related studies
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {marker.pmids.map((pmid) => (
                      <a
                        key={pmid}
                        href={`https://mensresearch.ai/research/${pmid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-md bg-slate-brand/10 px-2 py-0.5 text-xs text-slate-brand hover:bg-slate-brand/20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        PMID:{pmid}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
